import { cn } from '@virginexperiencedays/tailwind';
import { IconChevronRight } from '@virginexperiencedays/components-v2/src/icons/IconChevronRight';
import { IconCloseModal } from '@virginexperiencedays/components-v2/src/icons/IconCloseModal';
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@virginexperiencedays/components-v2/src/layout/Drawer';
import { LocationCount } from '@virginexperiencedays/components-v2/src/layout/Maps';
import { SectionHeading } from '@virginexperiencedays/components-v2/src/typography/SectionHeading';

import type { Regions } from '@virginexperiencedays/components-v2/src/pages/product/Locations';
import type { Location } from '@virginexperiencedays/products';

import { RegionLocationOptions } from './LocationOptions';

import type { ComponentPropsWithoutRef, ElementType } from 'react';

type RegionListDrawerProps = {
  isOpenModal: boolean;
  regions: Regions;
  regionNames: string[];
  locations: Location[];
  handleOpenChange: (value: boolean) => void;
  onClickOption: (name: string) => void;
  onClickAllLocation: () => void;
  activeRegion?: string;
};

export const RegionListDrawer = ({
  isOpenModal,
  activeRegion,
  regions,
  regionNames,
  locations,
  handleOpenChange,
  onClickOption,
  onClickAllLocation,
}: RegionListDrawerProps) => {
  return (
    <Drawer open={isOpenModal} onOpenChange={handleOpenChange} noBodyStyles disablePreventScroll>
      <DrawerContent className="md:hidden" hasHandle>
        <DrawerHeader className="flex items-center">
          <DrawerTitle className="flex-1">
            <SectionHeading>Locations</SectionHeading>
          </DrawerTitle>
          <DrawerClose className="px-2">
            <IconCloseModal />
          </DrawerClose>
        </DrawerHeader>
        <DrawerBody className="gap-4 pb-4 pt-0">
          <LocationCount locationCount={locations.length} locationName={locations[0]?.name} />

          <div
            className={cn(
              'flex flex-col',
              'overflow-y-auto !pr-0 mega-nav-scrollbar after:pb-10',
              // 90dvh less heights of drawer handle, section heading, location count,
              // the gap between location count and this, and drawer body padding bottom respectively
              'max-h-[calc(90dvh_-_(20px_+_54px_+_24px_+_16px_+_16px))]'
            )}
          >
            <RegionLocationOptions
              regions={regions}
              regionNames={regionNames}
              activeRegion={activeRegion}
              onClickAllLocation={onClickAllLocation}
              onClickOption={onClickOption}
            />
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export type RegionListDrawerTriggerProps<T extends ElementType = ElementType> = {
  as?: T;
  chevronDirection?: 'right' | 'down';
} & ComponentPropsWithoutRef<T>;

export const RegionListDrawerTrigger = ({
  as: Component = 'button',
  chevronDirection = 'right',
  children,
  className,
  ...rest
}: RegionListDrawerTriggerProps) => (
  <Component
    className={cn(
      'border-text-neutral-strong flex cursor-pointer items-center justify-between rounded border bg-background-page p-3.5 focus:outline-0',
      className
    )}
    {...rest}
  >
    <div className="flex flex-wrap items-center text-base font-semibold leading-none">
      <p className="line-clamp-1 text-ellipsis text-left leading-normal">{children}</p>
    </div>

    <IconChevronRight
      width="20"
      height="20"
      className={cn(
        'h-5 min-h-5 w-5 min-w-5',
        chevronDirection === 'down' && 'rotate-90 transform'
      )}
      aria-hidden="true"
    />
  </Component>
);
